import { Box } from '@mui/material';
import {
  JSXElementConstructor,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  ArrayPath,
  Control,
  FieldArrayWithId,
  FieldValues,
  PathValue,
  useFieldArray,
  UseFieldArrayReturn,
} from 'react-hook-form';

import { ButtonSize } from '@/components/form/baseInputs/Button/types';
import { CardFieldItem } from '@/components/form/formAwareInputs/FormAwareCardRepeater/CardFieldItem';
import { DuplicatedCardFieldGroup } from '@/components/form/formAwareInputs/FormAwareCardRepeater/DuplicatedCardFieldGroup';
import { AdditionalItemRendererProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import {
  DuplicatedFieldGroupWithoutArrayFieldFullPath,
  RepeaterInputShape,
} from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.types';
import { groupDuplicateFields } from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.utils';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useFormContext } from '@/components/react-hook-form';
import { EmptyListItem } from '@/components/typography/EmptyListItem';
import { ArrayFieldNameFromFormShape } from '@/types/react-hook-form';

import { Button } from '../../baseInputs/Button';
import { useFormFieldsDisabled } from '../../context/formFieldsDisabled.context';

export interface FormAwareCardRepeaterProps<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
> {
  name: ArrayFieldNameFromFormShape<FormShape>;
  control: Control<FormShape>;
  render: (
    itemIndex: number,
    fieldArrayProps: Pick<
      UseFieldArrayReturn<
        FormShape,
        ArrayFieldNameFromFormShape<FormShape>,
        FieldArrayKeyName
      >,
      'swap' | 'fields' | 'remove'
    >,
    // This id is the auto-generated id from the useFieldArray hook, to keep
    // track of the fields in the array.
    _id: string,
    additionalItemRendererProps?: AdditionalItemRendererProps
  ) => React.ReactNode;
  allowEmpty?: boolean;
  emptyValue: PathValue<FormShape, ArrayFieldNameFromFormShape<FormShape>>;
  addAnotherItemText: string;
  addAnotherButtonSize?: ButtonSize;
  showRemove: boolean;
  removeButtonText?: string;
  variant?: 'outlined' | 'filled' | 'transparent';
  shouldInit?: boolean;
  maxItems?: number;
  // This just applies to the bottom margin of each card item
  spacing?: number;

  // This is the key name for useFieldArray to use to store the auto-generated
  // id for each item in the array.
  fieldArrayKeyName?: FieldArrayKeyName;

  // This is the name of the field in the input shape that is used to identify
  // the input. This is used to identify and group duplicates.
  idField?: keyof RepeaterInputShape<FormShape>;
  shouldGroupDuplicates?: boolean;
  duplicatesRef?: React.RefObject<HTMLDivElement | null>;
  onAppend?: (
    fields: FieldArrayWithId<
      FormShape,
      ArrayPath<FormShape>,
      FieldArrayKeyName
    >[]
  ) => void;
}

function EmptyState() {
  return <EmptyListItem>No items</EmptyListItem>;
}

interface FormAwareCardRepeaterSlots {
  slots?: {
    EmptyState?: JSXElementConstructor<unknown>;
  };
}

function useSlots({
  slots,
}: FormAwareCardRepeaterSlots): Required<
  NonNullable<FormAwareCardRepeaterSlots['slots']>
> {
  return { EmptyState, ...slots };
}

export function FormAwareCardRepeater<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string = '_id',
>({
  allowEmpty,
  emptyValue,
  name,
  control,
  addAnotherItemText,
  addAnotherButtonSize = 'sm',
  showRemove,
  variant = 'filled',
  render,
  removeButtonText,
  shouldInit,
  maxItems = Infinity,
  spacing = 2,
  slots: externalSlots,
  fieldArrayKeyName = '_id' as FieldArrayKeyName,
  idField = 'id',
  shouldGroupDuplicates = false,
  duplicatesRef,
  onAppend,
}: FormAwareCardRepeaterProps<FormShape, FieldArrayKeyName> &
  FormAwareCardRepeaterSlots) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const slots = useSlots({
    slots: externalSlots,
  });
  const needsInitialization = useRef(shouldInit);
  const { reset } = useFormContext();
  const [triggerAppend, setTriggerAppend] = useState(0);
  const { fields, append, remove, swap } = useFieldArray({
    control,
    name,
    keyName: fieldArrayKeyName,
    rules: {
      maxLength: maxItems,
    },
  });

  useEffect(() => {
    if (fields.length === 0 && needsInitialization.current) {
      append(emptyValue, { shouldFocus: false });
      // This is a hack to prevent appending on form initialization
      // from setting the form state to dirty
      reset({}, { keepValues: true });
    }
    // Always mark initialization as complete after first render,
    // regardless of whether we added an empty item. This prevents
    // re-adding an empty item when the user removes all items from
    // an existing entity that started with items.
    needsInitialization.current = false;
  }, [
    append,
    fields.length,
    needsInitialization,
    emptyValue,
    name,
    reset,
    allowEmpty,
  ]);

  const showDeleteButton = useMemo(() => {
    if (allowEmpty) {
      return showRemove;
    }

    return showRemove && fields.length > 1;
  }, [allowEmpty, showRemove, fields.length]);

  const groupedFields: DuplicatedFieldGroupWithoutArrayFieldFullPath<
    FormShape,
    FieldArrayKeyName
  >[] = useMemo(
    () =>
      groupDuplicateFields<FormShape, FieldArrayKeyName>(
        fields,
        idField,
        fieldArrayKeyName,
        shouldGroupDuplicates
      ),
    [fieldArrayKeyName, fields, idField, shouldGroupDuplicates]
  );

  const handleAdd = useCallback(() => {
    append(emptyValue, { shouldFocus: true });
    setTriggerAppend((prev) => prev + 1);
  }, [append, emptyValue]);

  useEffect(
    () => {
      if (triggerAppend > 0) {
        onAppend?.(fields);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerAppend]
  );

  return (
    <Box>
      <Box>
        {groupedFields.map((group) => {
          const { id, suggestedFieldWithIndex, existingFieldWithIndex } = group;

          const commonProps = {
            render,
            fieldArrayProps: { swap, fields, remove },
            showDeleteButton,
            removeButtonText,
            variant,
            spacing,
          };

          if (suggestedFieldWithIndex && existingFieldWithIndex) {
            return duplicatesRef?.current
              ? createPortal(
                  <DuplicatedCardFieldGroup<FormShape, FieldArrayKeyName>
                    key={id} // This is the idField value
                    fieldArrayKeyName={fieldArrayKeyName}
                    group={{
                      id,
                      suggestedFieldWithIndex,
                      existingFieldWithIndex,
                      arrayFieldFullPath: name,
                    }}
                    {...commonProps}
                  />,
                  duplicatesRef?.current
                )
              : null;
          }

          const fieldWithIndex =
            suggestedFieldWithIndex || existingFieldWithIndex;

          if (!fieldWithIndex) return null;

          return (
            <CardFieldItem<FormShape, FieldArrayKeyName>
              key={fieldWithIndex.field[fieldArrayKeyName]}
              i={fieldWithIndex.index}
              value={fieldWithIndex.field}
              fieldArrayKeyName={fieldArrayKeyName}
              {...commonProps}
            />
          );
        })}
        {fields.length === 0 && allowEmpty && <slots.EmptyState />}
      </Box>
      {fields.length < maxItems && (
        <Button
          size={addAnotherButtonSize}
          fullWidth
          variant="secondary"
          disabled={disabledFromContext}
          startIcon={PlusIcon}
          onClick={handleAdd}
        >
          {addAnotherItemText}
        </Button>
      )}
    </Box>
  );
}
